/**
 * Catálogo de PERFILES DE SUMMON — fuente única de verdad (client + server).
 *
 * Cada perfil describe TODO sobre una invocación (template, cantidad, canal,
 * duración, reserva, resurrección, buffs al owner, FX). Las fuentes de summon
 * (spells toggle, items equipados/usables, consumibles) sólo referencian un
 * `summonProfileId` de este catálogo.
 *
 * Convención: IDs internos en inglés, display (`nameEs`) en español.
 * Ver docs/SUMMONS.md.
 */

import type { SummonProfile } from '../types/summon';
import { INVISIBILITY } from '../constants';

export const SUMMON_PROFILES: Record<string, SummonProfile> = {
  // ===========================================================================
  // MINION CORE DEL BRUJO — toggle con reserva de maná (permanente + revive)
  // ===========================================================================
  summon_wolf: {
    id: 'summon_wolf',
    nameEs: 'Lobo Espiritual',
    npcTemplateId: 'summon_wolf',
    count: 1,
    role: 'hostile',
    castTimeMs: 2000,
    durationMs: 0, // permanente: vive hasta disipar/morir
    manaReservePercent: 0.2, // reserva 20% del maná mientras esté invocado
    respawnMs: 8000, // revive 8s después de morir (reducible por summonResurrectionSpeed)
    fx: {
      channel: 'fx_arcane_swirl',
      channelEnd: 'fx_summon_portal',
      onEntity: 'fx_arcane_swirl',
      onDeath: 'fx_ice_spark_impact',
    },
  },

  // ===========================================================================
  // SUMMONS TIMED (burst, costo único) — reservados para fases siguientes.
  // Se dejan definidos para que los spells que los referencian queden consistentes.
  // ===========================================================================
  summon_skeleton: {
    id: 'summon_skeleton',
    nameEs: 'Esqueleto Guerrero',
    npcTemplateId: 'summon_skeleton',
    count: 2,
    role: 'hostile',
    castTimeMs: 1500,
    durationMs: 90000,
    respawnMs: 0,
    fx: {
      channelEnd: 'fx_summon_portal',
      onDeath: 'fx_dark_curse_aura',
    },
  },
  summon_golem: {
    id: 'summon_golem',
    nameEs: 'Golem de Piedra',
    npcTemplateId: 'summon_golem',
    count: 1,
    role: 'hostile',
    castTimeMs: 3000,
    durationMs: 180000,
    respawnMs: 0,
    fx: {
      channelEnd: 'fx_summon_portal',
    },
  },

  // ===========================================================================
  // SUMMONS POR ITEM (Fase 2)
  // ===========================================================================

  // Amuleto equipado (grant): guardián permanente mientras el amuleto esté puesto.
  summon_guardian: {
    id: 'summon_guardian',
    nameEs: 'Guardián de Piedra',
    npcTemplateId: 'summon_guardian',
    count: 1,
    role: 'hostile',
    castTimeMs: 0,
    durationMs: 0, // permanente (atado al equip)
    respawnMs: 10000,
    fx: {
      channelEnd: 'fx_summon_portal',
      onEntity: 'fx_shield_dome_aura',
    },
  },
  // Amuleto usable (cooldown): sabueso temporal de 30s.
  summon_warhound: {
    id: 'summon_warhound',
    nameEs: 'Sabueso de Guerra',
    npcTemplateId: 'summon_warhound',
    count: 1,
    role: 'hostile',
    castTimeMs: 0,
    durationMs: 30000,
    respawnMs: 0,
    fx: {
      channelEnd: 'fx_summon_portal',
      onDeath: 'fx_ice_spark_impact',
    },
  },
  // Poción consumible: diablillo temporal de 25s.
  summon_imp: {
    id: 'summon_imp',
    nameEs: 'Diablillo',
    npcTemplateId: 'summon_imp',
    count: 1,
    role: 'hostile',
    castTimeMs: 0,
    durationMs: 25000,
    respawnMs: 0,
    fx: {
      channelEnd: 'fx_summon_portal',
      onEntity: 'fx_dark_curse_aura',
      onDeath: 'fx_fire_burst_impact',
    },
  },
  // Poción consumible: enjambre de 2 arañas temporales de 20s.
  summon_spider: {
    id: 'summon_spider',
    nameEs: 'Araña Invocada',
    npcTemplateId: 'summon_spider',
    count: 2,
    role: 'hostile',
    castTimeMs: 0,
    durationMs: 20000,
    respawnMs: 0,
    fx: {
      channelEnd: 'fx_summon_portal',
    },
  },

  // ===========================================================================
  // SUMMONS DE SOPORTE (Fase 4) — no atacan; castean sobre el owner con cooldown.
  // ===========================================================================

  // Sanador permanente (amuleto grant): cura al owner cada ~4s.
  summon_healer: {
    id: 'summon_healer',
    nameEs: 'Espíritu Sanador',
    npcTemplateId: 'summon_healer',
    count: 1,
    role: 'support',
    castTimeMs: 0,
    durationMs: 0,
    respawnMs: 12000,
    supportSpellIds: ['spell_curar_heridas'],
    supportIntervalMs: 4000,
    fx: {
      channelEnd: 'fx_priest_heal_burst',
      onEntity: 'fx_holy_circle_aura',
      onDeath: 'fx_resurrection_glow',
    },
  },
  // Provocador (tanque): NO cura; cada ~4s provoca a los enemigos cercanos a SÍ MISMO
  // (jala aggro hacia él). Reusa el template del Guardián. Ver docs/TAUNT.md.
  summon_provocateur: {
    id: 'summon_provocateur',
    nameEs: 'Guardián Provocador',
    npcTemplateId: 'summon_guardian',
    count: 1,
    role: 'support',
    castTimeMs: 0,
    durationMs: 0,
    respawnMs: 12000,
    taunt: { radius: 3, intervalMs: 4000, durationMs: 3000 },
    fx: {
      channelEnd: 'fx_summon_portal',
      onEntity: 'fx_holy_circle_aura',
      onDeath: 'fx_resurrection_glow',
    },
  },
  // Liberador temporal (poción): quita inmovilizar/paralizar cada ~3s.
  summon_liberator: {
    id: 'summon_liberator',
    nameEs: 'Espíritu Liberador',
    npcTemplateId: 'summon_liberator',
    count: 1,
    role: 'support',
    castTimeMs: 0,
    durationMs: 45000,
    respawnMs: 0,
    supportSpellIds: ['spell_liberar'],
    supportIntervalMs: 3000,
    fx: {
      channelEnd: 'fx_summon_portal',
      onEntity: 'fx_arcane_swirl',
    },
  },
  // Bardo temporal (poción): mantiene un buff de daño/vel. ataque sobre el owner.
  summon_bard: {
    id: 'summon_bard',
    nameEs: 'Duende Bardo',
    npcTemplateId: 'summon_bard',
    count: 1,
    role: 'support',
    castTimeMs: 0,
    durationMs: 45000,
    respawnMs: 0,
    supportSpellIds: ['spell_grito_guerra'],
    supportIntervalMs: 2500,
    fx: {
      channelEnd: 'fx_summon_portal',
      onEntity: 'fx_fire_ring_aura',
    },
  },
  // Portador de maná permanente (amuleto grant): restaura maná del owner.
  summon_manacarrier: {
    id: 'summon_manacarrier',
    nameEs: 'Espíritu de Maná',
    npcTemplateId: 'summon_manacarrier',
    count: 1,
    role: 'support',
    castTimeMs: 0,
    durationMs: 0,
    respawnMs: 12000,
    supportSpellIds: ['spell_bendicion_mana'],
    supportIntervalMs: 8000,
    fx: {
      channelEnd: 'fx_summon_portal',
      onEntity: 'fx_frost_orbit_aura',
    },
  },
  // Centinela Vidente (Clérigo): invocación ESTÁTICA (moveSpeed 0 en su template) con 100 de
  // vida que castea el Pulso Revelador seguido para detectar invisibles cerca. Ver docs/INVISIBILITY.md.
  summon_sentinel: {
    id: 'summon_sentinel',
    nameEs: 'Centinela Vidente',
    npcTemplateId: 'summon_sentinel',
    count: 1,
    role: 'support',
    castTimeMs: 0,
    durationMs: 0,
    respawnMs: 0,
    pulseSpellId: 'spell_detectar_invisibilidad_pulso',
    pulseIntervalMs: 8000,
    fx: {
      channelEnd: 'fx_summon_portal',
      onEntity: 'fx_arcane_swirl',
    },
  },
  // Espíritu Vidente del Bosque (Clérigo): sigue al owner y castea el Pulso Revelador cada 2 min
  // (detección de área más esporádica pero móvil). Ver docs/INVISIBILITY.md.
  summon_seer_spirit: {
    id: 'summon_seer_spirit',
    nameEs: 'Espíritu Vidente del Bosque',
    npcTemplateId: 'summon_seer_spirit',
    count: 1,
    role: 'support',
    castTimeMs: 0,
    durationMs: 0,
    respawnMs: 0,
    pulseSpellId: 'spell_detectar_invisibilidad_pulso',
    pulseIntervalMs: INVISIBILITY.FOREST_DETECT_INTERVAL_MS,
    fx: {
      channelEnd: 'fx_summon_portal',
      onEntity: 'fx_arcane_swirl',
    },
  },
};

/** Devuelve el perfil de summon por id (o undefined si no existe). */
export function getSummonProfile(id: string): SummonProfile | undefined {
  return SUMMON_PROFILES[id];
}
